import { MAX_DURATION_MONTHS, MAX_DURATION_WEEKS } from "../Constant";

const MINIMUM_SELECTABLE_INCREMENT_VALIDATION_CONFIG = {
  month: 120,
  week: 521,
};

export function isNumeric(str: string | number) {
  str = str?.toString(); // Ensure the value is a string
  return !isNaN(Number(str)) && !isNaN(parseFloat(str));
}

export const trimString = (value: string) => {
  return value.trim();
};

export const ltrimString = (value: string) => {
  return value.trimStart();
};

export const isValidEmail = (val: string): boolean => {
  return /^[a-zA-Z0-9][a-zA-Z0-9-_\.]+@([a-zA-Z]|[a-zA-Z0-9]?[a-zA-Z0-9-]+[a-zA-Z0-9])\.[a-zA-Z0-9]{2,10}(?:\.[a-zA-Z]{2,10})?$/.test(
    val,
  );
};

export const isValidUrl = (val: string): boolean => {
  // eslint-disable-next-line no-useless-escape
  const res = val.match(
    /^(https?:\/\/|www\.)[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)?$/,
  );
  return res !== null;
};

export const isStringHavingSpecialChars = (val: string) => {
  //const regex = /[!#^*\-+={}[\]:;<>?\/|\\]/;
  const regex = /[!#^\{}[\]:;<>?\/|\\]/;
  return regex.test(val);
};

export const isInputStringValid = (val: string) => {
  // regex to allow alphanumeric characters, spaces and specific special characters (including !#[]{}\|),
  const regex = /(^$|^[a-zA-Z0-9\s'"!`@#$%^&*()+=\-[\]',./{}|\\:<>?~_ ]+$)/;
  return regex.test(val);
};

export const isEmpty = (value: string | number | undefined | null) => {
  return value === "" || value === null || value === undefined;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function convertHtmlToText(html: any) {
  const tempElement = document.createElement("div");
  tempElement.innerHTML = html;
  return tempElement.innerText || tempElement.textContent || "";
}

export const isHTML = RegExp.prototype.test.bind(/(<([^>]+)>)/i);

export const requiredValidationForEditor = (val: any) => {
  let value: any;
  if (isEmpty(val) || val.length === 0) {
    return false;
  }
  if (isHTML(val)) {
    value = convertHtmlToText(val).trim();
  } else {
    value = Array.isArray(val)
      ? val.map((item) => ltrimString("" + item))
      : ltrimString("" + val);
  }
  if (isEmpty(value) || value.length == 0) {
    return false;
  }
  return true;
};

export const requiredValidationForFields = (val: any) => {
  let value: any;
  if (isEmpty(val) || val.length === 0) {
    return { isValid: false, hasHtml: false };
  }
  if (isHTML(val)) {
    return { isValid: false, hasHtml: true };
  } else {
    value = Array.isArray(val)
      ? val.map((item) => ltrimString("" + item))
      : ltrimString("" + val);
  }
  if (isEmpty(value) || value.length == 0) {
    return { isValid: false, hasHtml: false };
  }
  return { isValid: true, hasHtml: false };
};

export const validateCostInterval = (
  costIntervalCode: string,
  orderInterval: number,
  costIntervalMin: number,
): boolean => {
  if (costIntervalCode === "month") {
    return (
      orderInterval >= costIntervalMin && orderInterval <= MAX_DURATION_MONTHS
    );
  }

  if (costIntervalCode === "week") {
    return (
      orderInterval >= costIntervalMin && orderInterval <= MAX_DURATION_WEEKS
    );
  }

  return true;
};

export const notZeroValidation = (val: number) => {
  if (val === 0) {
    return false;
  }
  return true;
};

export const isPositiveValidation = (val: number) => {
  if (val <= 0) {
    return false;
  }
  return true;
};

export const validateMinimumSelectableIncrement = (
  value: number,
  period: "month" | "week",
): boolean => {
  if (!value) return false;

  return value <= (MINIMUM_SELECTABLE_INCREMENT_VALIDATION_CONFIG[period] ?? 0);
};

export const getMinimumSelectableIncrementErrorMessage = (
  period: "month" | "week",
): string => {
  return period === "month"
    ? "Minimum selectable increment cannot exceed 120 months as per Commercial Hub limits."
    : "Minimum selectable increment cannot exceed 521 weeks, as per Commercial Hub limits.";
};

export const isValidUrlWithWWW = (string: string) => {
  try {
    const val = string.trim().toLowerCase();

    // Remove protocol if present
    let withoutProtocol = val.replace(/^https?:\/\//, "");
    // Remove userinfo if present (anything before @)
    if (withoutProtocol.includes("@")) {
      withoutProtocol = withoutProtocol.split("@")[1];
    }

    // Remove everything after first slash (path, query, etc.)
    const domainPart = withoutProtocol.split("/")[0];

    // Split by dot
    const parts = domainPart.split(".");

    // If first part is 'www', remove it
    if (parts[0].toLowerCase() === "www") {
      parts.shift();
    }

    // Validate: must have at least 2 parts (domain + TLD)
    if (parts.length < 2) return false;

    const domain = parts[0];
    const tld = parts[parts.length - 1];

    // Check domain and TLD rules
    const domainValid = /^[a-zA-Z0-9-]+$/.test(domain);
    const tldValid = /^[a-zA-Z]{2,}$/.test(tld);

    return domainValid && tldValid;
  } catch {
    return false;
  }
};

export const htmlToTextWithEnter = (html: string) => {
  let text = html.replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div)>/gi, "\n");
  text = text.replace(/<[^>]+>/g, "");
  text = text.trim();
  return text;
};

export const isFileNameContainsDangerousExtensions = (filename: string) => {
  // Reject dangerous intermediate extensions
  const dangerousExts = [
    "php",
    "asp",
    "aspx",
    "jsp",
    "exe",
    "bat",
    "sh",
    "dll",
  ];
  const parts = filename.toLowerCase().split(".");
  // Check extensions before the final extension
  const intermediateExts = parts.slice(1, -1);
  return !intermediateExts.some((ext) => dangerousExts.includes(ext));
};
